import {
  Box,
  FileButton,
  Flex,
  Text,
  Paper,
  Title,
  Modal,
  Image,
  SimpleGrid,
  Skeleton,
  Loader,
  ActionIcon,
} from "@mantine/core";
import { IconUpload, IconEye, IconTrash } from "@tabler/icons-react";
import { useState, useRef, useEffect } from "react";
import { FileCategory } from "../shared/constants/general.const";
import trpc from "@/shared/utils/trpc";
import { errorNotification } from "@/shared/utils/errorNotification";
import { successNotification } from "@/shared/utils/successNotification";
import { getFileUrls } from "@/shared/utils/useFileUrl";

interface ImagesTabContentProps {
  company: any;
  refreshCompany: () => Promise<void>;
  isViewMode?: boolean;
  anonymousView?: boolean;
  activeTab?: string | null;
  aiImages?: string[];
  removeAiImage?: (index: number) => void;
}

interface ImageWithUrl {
  id: string;
  url: string | null;
  loading: boolean;
  filename?: string;
  originalName?: string;
  createdAt?: string;
}

export default function ImagesTabContent({
  company,
  refreshCompany,
  isViewMode = false,
  anonymousView = false,
  activeTab = null,
  aiImages = [],
  removeAiImage,
}: ImagesTabContentProps) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imagesWithUrls, setImagesWithUrls] = useState<ImageWithUrl[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const resetRef = useRef<() => void>(null);

  // Filter additional images
  const images = (company?.images || []).filter((file: any) => file.category === FileCategory.ADDITIONAL_IMAGES);

  // Load all image URLs (skip if anonymous view since we only show filenames)
  useEffect(() => {
    // Skip loading URLs for anonymous view - we only show filenames
    if (anonymousView) {
      setInitialLoading(false);
      return;
    }

    // Only load images when the Images tab is active
    if (activeTab !== "images") {
      setInitialLoading(false);
      return;
    }

    const loadImageUrls = async () => {
      setInitialLoading(true);

      try {
        const fileIds = images.map((file: any) => file.id);
        const urlMap = await getFileUrls(fileIds);

        const loadedImages = images.map((file: any) => ({
          id: file.id,
          url: urlMap[file.id] || null,
          loading: false,
          filename: file.filename,
          originalName: file.originalName,
          createdAt: file.createdAt,
        }));

        setImagesWithUrls(loadedImages);
      } catch (err) {
        console.error("Failed to load images batch:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    if (images.length > 0) {
      loadImageUrls();
    } else {
      setInitialLoading(false);
      setImagesWithUrls([]);
    }
  }, [images.length, company?.id, anonymousView, activeTab]);

  const handleUpload = async (files: File[]) => {
    if (!company?.id) return;
    setUploading(true);

    try {
      const uploadPromises = files.map((file) => {
        // Validation per file
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File ${file.name} exceeds 10MB limit`);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", FileCategory.ADDITIONAL_IMAGES);
        formData.append("companyId", company.id);
        return trpc.file.upload.mutate(formData);
      });

      await Promise.all(uploadPromises);
      successNotification({ title: "Success", message: `${files.length} image(s) uploaded successfully` });
      await refreshCompany();
    } catch (err: any) {
      console.error(err);
      errorNotification({ title: "File too large", message: err.message || "Failed to upload images" });
    } finally {
      resetRef.current?.();
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    setDeletingId(fileId);
    try {
      await trpc.file.delete.mutate({ fileId });
      successNotification({ title: "Success", message: "Image deleted" });
      await refreshCompany();
    } catch (err: any) {
      console.error(err);
      errorNotification({ title: "Error", message: err.message || "Failed to delete image" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewImage = (url: string) => {
    setPreviewImage(url);
  };

  return (
    <Box>
      <Modal
        opened={!!previewImage}
        onClose={() => setPreviewImage(null)}
        size="auto"
        title="Image Preview"
        centered
        styles={{
          body: { padding: 0, overflow: "hidden" },
          content: { maxHeight: "100vh", overflow: "hidden" },
        }}
      >
        {previewImage && (
          <Image
            src={previewImage}
            alt="Preview"
            radius="md"
            fit="contain"
            style={{
              maxHeight: "80vh",
              maxWidth: "90vw",
              width: "auto",
              height: "auto",
              display: "block",
            }}
          />
        )}
      </Modal>

      {!isViewMode && (
        <Box mb="xl">
          <Title order={4} mb={4} fw={400}>
            Images
          </Title>
          <Text fz="sm" c="slate.6">
            Upload images realted to your product and services.
          </Text>
        </Box>
      )}

      {!isViewMode && (
        <FileButton resetRef={resetRef} onChange={handleUpload} accept="image/png,image/jpeg" multiple>
          {(props) => (
            <Paper
              {...props}
              p="xl"
              withBorder
              radius="md"
              mb="xl"
              style={{
                border: "1px solid #E5E7EB",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
              }}
            >
              {uploading ? (
                <Text>Uploading...</Text>
              ) : (
                <>
                  <Box
                    bg="blue.0"
                    p="md"
                    style={{ borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}
                    mb="md"
                  >
                    <IconUpload size={24} color="#2563EB" />
                  </Box>
                  <Text size="md" fw={500} mb={4}>
                    Click to upload or drag and drop
                  </Text>
                  <Text size="sm" c="slate.6">
                    PNG, or JPG • Max 10MB
                  </Text>
                </>
              )}
            </Paper>
          )}
        </FileButton>
      )}

      {/* Anonymous View - Filename Grid */}
      {anonymousView && (
        <>
          {images.length === 0 && (
            <Paper p="xl" radius="lg" style={{ border: "1px solid #E5E7EB" }}>
              <Text c="slate.5" ta="center">
                No images uploaded
              </Text>
            </Paper>
          )}

          <SimpleGrid cols={6} spacing="md">
            {images.map((file: any, index: number) => (
              <Paper
                key={file.id}
                p="sm"
                radius="md"
                style={{ border: "1px solid #E5E7EB", backgroundColor: "#F4F4F533" }}
              >
                <Text fw={500} size="xs" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {`Image ${index + 1}`}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </>
      )}

      {/* Authenticated View - Grid View */}
      {!anonymousView && (
        <>
          {/* Empty state */}
          {!initialLoading && images.length === 0 && isViewMode && (
            <Paper p="xl" radius="lg" style={{ border: "1px solid #E5E7EB" }}>
              <Text c="slate.5" ta="center">
                No images uploaded
              </Text>
            </Paper>
          )}

          {/* Skeleton loading state */}
          {initialLoading && (
            <SimpleGrid cols={6} spacing="md">
              {[...Array(6)].map((_, index) => (
                <Skeleton key={index} height={130} radius="md" />
              ))}
            </SimpleGrid>
          )}

          {/* AI generated image grid */}
          {aiImages.length > 0 && (
            <SimpleGrid cols={6} spacing="md" mb="md">
              {aiImages.map((url, index) => (
                <Box
                  key={`ai-${index}`}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  <Image
                    src={url}
                    alt={`AI Image ${index + 1}`}
                    fit="cover"
                    style={{ width: "100%", height: "100%" }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "0";
                    }}
                  >
                    <ActionIcon
                      variant="filled"
                      color="blue"
                      size="lg"
                      radius="md"
                      onClick={() => handleViewImage(url)}
                    >
                      <IconEye size={20} />
                    </ActionIcon>
                    {!isViewMode && removeAiImage && (
                      <ActionIcon
                        variant="filled"
                        color="red"
                        size="lg"
                        radius="md"
                        onClick={() => removeAiImage(index)}
                      >
                        <IconTrash size={20} />
                      </ActionIcon>
                    )}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          )}

          {/* Image grid */}
          {!initialLoading && imagesWithUrls.length > 0 && (
            <SimpleGrid cols={6} spacing="md">
              {imagesWithUrls.map((image) => (
                <Box
                  key={image.id}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  {image.url ? (
                    <>
                      <Image
                        src={image.url}
                        alt={image.filename || image.originalName || "Company Image"}
                        fit="cover"
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      />
                      {/* Hover overlay with actions */}
                      <Box
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                        className="image-overlay"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = "1";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = "0";
                        }}
                      >
                        <ActionIcon
                          variant="filled"
                          color="blue"
                          size="lg"
                          radius="md"
                          onClick={() => handleViewImage(image.url!)}
                        >
                          <IconEye size={20} />
                        </ActionIcon>
                        {!isViewMode && (
                          <ActionIcon
                            variant="filled"
                            color="red"
                            size="lg"
                            radius="md"
                            onClick={() => handleDelete(image.id)}
                            loading={deletingId === image.id}
                          >
                            <IconTrash size={20} />
                          </ActionIcon>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Loader size="sm" />
                    </Flex>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          )}
        </>
      )}
    </Box>
  );
}
